import { useRequest } from 'features/request/useRequest';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import { useEffect, useMemo } from 'react';

import type { IVariablesChangeData } from '../../WorkOrderUseCoefficient';
import {
    uerWorkOrderChangePriceListRatioForObjects,
    uerWorkOrderChangePriceListRatioForRootObject,
} from '../GridPickCoefficientsQueries';

export const useWorkOrderChangeGridPickData = (variablesChangeData: IVariablesChangeData) => {
    const {
        isRootItem,
        workOrderChangeId,
        constructionTypeId,
        objectIds = [],
        rootObjectIds = {},
    } = variablesChangeData || {};

    const variablesForRootObject = useMemo(
        () => ({
            workOrderChangeId,
            constructionTypeId,
            rootObjectIds,
        }),
        [constructionTypeId, rootObjectIds, workOrderChangeId],
    );

    const variablesForObjects = useMemo(
        () => ({
            workOrderChangeId,
            constructionTypeId,
            objectIds,
        }),
        [constructionTypeId, objectIds, workOrderChangeId],
    );

    const [priceListForRootObjectData, priceListForRootObjectStatus, , priceListForRootObjectRequest] = useRequest({
        requestId: 'priceWorkOrderChangeListRatioForRootObject',
        query: uerWorkOrderChangePriceListRatioForRootObject,
        withEffect: false,
        variables: variablesForRootObject,
    });

    const [priceListForObjectsData, priceListForObjectsStatus, , priceListForObjectRequest] = useRequest({
        requestId: 'priceWorkOrderChangeListRatioForObjects',
        query: uerWorkOrderChangePriceListRatioForObjects,
        withEffect: false,
        variables: variablesForObjects,
    });

    const orderChangeFetchingStatus = useMemo(
        () => (isRootItem && priceListForRootObjectStatus) || (!isRootItem && priceListForObjectsStatus),
        [isRootItem, priceListForRootObjectStatus, priceListForObjectsStatus],
    );

    const requestId = isRootItem
        ? 'priceWorkOrderChangeListRatioForRootObject'
        : 'priceWorkOrderChangeListRatioForObjects';
    const requestFn = isRootItem ? priceListForRootObjectRequest : priceListForObjectRequest;
    const variables = isRootItem ? variablesForRootObject : variablesForObjects;

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: requestFn as any,
        requestVariables: variables,
    });

    useEffect(() => {
        if (priceListForRootObjectData?.uerWorkOrderChangePriceListRatioForRootObject?.items && getRowParams?.current) {
            const items = priceListForRootObjectData?.uerWorkOrderChangePriceListRatioForRootObject?.items;

            getRowParams?.current?.successCallback(
                items,
                priceListForRootObjectData?.uerWorkOrderChangePriceListRatioForRootObject?.totalCount,
            );
        }

        if (priceListForObjectsData?.uerWorkOrderChangePriceListRatioForObjects?.items && getRowParams?.current) {
            const items = priceListForObjectsData?.uerWorkOrderChangePriceListRatioForObjects?.items;

            getRowParams?.current?.successCallback(
                items,
                priceListForObjectsData?.uerWorkOrderChangePriceListRatioForObjects?.totalCount,
            );
        }
    }, [
        getRowParams,
        priceListForObjectsData?.uerWorkOrderChangePriceListRatioForObjects?.items,
        priceListForObjectsData?.uerWorkOrderChangePriceListRatioForObjects?.totalCount,
        priceListForRootObjectData?.uerWorkOrderChangePriceListRatioForRootObject?.items,
        priceListForRootObjectData?.uerWorkOrderChangePriceListRatioForRootObject?.totalCount,
    ]);

    return {
        orderChangeDatasource: dataSource,
        orderChangeFetchingStatus,
    };
};
